/**
 * The typed HTTP client every platform talks to the LunaPin server through.
 *
 * Constructed once by an app's bootstrap and handed to `:client`'s
 * `StorageRepository`, which is the only caller. Keeping the transport here rather
 * than in `:client` means a view model never mentions HTTP, and a future platform
 * gets the same wire behaviour for free.
 *
 * Behind an interface so a test — or a future offline/demo mode — can answer from
 * memory without a server, and `StorageRepository` cannot tell the difference.
 *
 * @see ApiRoutes
 */
package se.soderbjorn.lunapin.clientserver

import io.ktor.client.HttpClient
import io.ktor.client.HttpClientConfig
import io.ktor.client.call.body
import io.ktor.client.plugins.DefaultRequest
import io.ktor.client.plugins.HttpResponseValidator
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.HttpTimeoutConfig
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.serialization.kotlinx.json.json
import kotlin.coroutines.cancellation.CancellationException
import kotlinx.serialization.json.Json

/**
 * Build the [HttpClient] backing an [HttpLunaPinApi].
 *
 * No engine is named: each target has exactly one engine on its classpath (JS for
 * the browser bundle, CIO for the JVM, OkHttp on Android, Darwin on Apple), so
 * Ktor resolves it through the service loader and no code branches on platform.
 * `ignoreUnknownKeys` keeps an older cached bundle from failing to parse a
 * response that a newer server has added a field to.
 *
 * ── One function with defaults, where there used to be overloads ──────────────
 *
 * This was three overloads, and their header argued the reason: **Kotlin default arguments
 * do not cross into Swift** — Kotlin/Native exports one initializer carrying every parameter
 * — so a defaulted form would have deleted the two-argument call the iOS app was making.
 *
 * **No Swift call site remains.** LPN-21 gave both phones `LunaPinClient`, which builds their
 * transport in shared code, and the last Swift caller went with the greeting screen. The
 * argument that forced overloads is therefore spent, and LPN-27 is where keeping them stopped
 * being free: an origin-carrying client for the browser and a session-carrying one for the
 * phones are both `(String) -> HttpClient`, which is not two overloads, it is one signature
 * twice. The parameters collapse into one function rather than being disambiguated by names
 * nobody would guess right.
 *
 * ── Why every header goes on the client rather than on each request ───────────
 *
 * One `install`, and every present and future route gets it. Adding either header at each of
 * [HttpLunaPinApi]'s call sites would be seventeen places for the eighteenth route to be
 * forgotten in, and both failure modes are quiet: a forgotten bearer is a 401 on one endpoint
 * only, which reads as a server bug, and a forgotten origin is that route's writes echoing
 * back to their own author — a re-read landing mid-gesture.
 *
 * @param sessionId the opaque id the server minted, from [SignInResponse.sessionId], presented
 *   as `Authorization: Bearer` on every request. For a caller with no cookie jar — the Android
 *   and iOS apps. **The browser must leave this null**: its session is an `HttpOnly` cookie the
 *   browser attaches itself, and page script cannot read it, which is the point. **Never
 *   logged and never put in a URL**, because unlike a query string a header does not end up in
 *   access logs, referrer headers or browser history.
 * @param originId from [newOriginId], naming this client so it can ignore the echo of its own
 *   writes. Generated once per app instance and **shared with whatever reads the event
 *   stream** — two different values would defeat the mechanism entirely, since the stream
 *   would never recognise the writer's own events. Null for a caller that makes no writes
 *   anybody would hear about, which is the sign-in probe and nothing else.
 * @param onUnauthorized called for every 401, on the coroutine that made the request, which
 *   may be any thread. Must be cheap and must not block; the shared model's version only sets
 *   a flag. **It observes and does not interfere** — the refusal goes on to become an
 *   [ApiFailure] carrying the server's own sentence, which every surface downstream reads.
 * @return a configured client the caller owns and is responsible for closing. A client built
 *   for one session is only valid for that session: when the session is replaced, build
 *   another rather than mutating this one.
 */
fun createHttpClient(
    sessionId: String? = null,
    originId: String? = null,
    onUnauthorized: (() -> Unit)? = null,
): HttpClient = HttpClient {
    lunaPinDefaults()
    if (sessionId != null || originId != null) {
        install(DefaultRequest) {
            if (sessionId != null) header(HttpHeaders.Authorization, "Bearer $sessionId")
            if (originId != null) header(LUNAPIN_ORIGIN_HEADER, originId)
        }
    }
    if (onUnauthorized != null) reportUnauthorized(onUnauthorized)
}

/**
 * A client for the one request that is meant to stay open: the `/api/events` stream.
 *
 * **A separate client, and a differently configured one.** The difference is not tidiness — an
 * engine's default read timeout closes a connection that has said nothing recently, and a
 * healthy event stream says nothing for as long as nothing changes. OkHttp's default is ten
 * seconds. Without the timeouts below, a phone would watch the stream drop and reconnect
 * perpetually, which looks exactly like a flaky network and is entirely self-inflicted.
 *
 * The server's twenty-second heartbeat exists to make a *genuinely* dead connection fail. That
 * is the opposite problem, and both mechanisms are needed: the heartbeat makes silence
 * detectable, and these timeouts stop silence being mistaken for failure in the meantime.
 *
 * It is also a separate client for the reason `SyncSetupBackingViewModel`'s factory names in
 * advance — *"the line to revisit is when something calls the server continuously"*. A
 * permanent occupant does not belong in a pool sized for requests that finish.
 *
 * @param sessionId as [createHttpClient]. Null for the browser, which does not use this at all
 *   because `EventSource` is its carrier; non-null for the phones.
 * @param onUnauthorized as [createHttpClient]. A lapsed session refuses the stream exactly as
 *   it refuses everything else, and it must badge the cogwheel the same way.
 */
fun createEventStreamHttpClient(
    sessionId: String? = null,
    onUnauthorized: (() -> Unit)? = null,
): HttpClient = HttpClient {
    lunaPinDefaults()
    install(HttpTimeout) {
        // Disabled, both of them. A stream has no request duration and no expected gap between
        // bytes; the heartbeat and the TCP stack are what notice a connection that has died.
        requestTimeoutMillis = HttpTimeoutConfig.INFINITE_TIMEOUT_MS
        socketTimeoutMillis = HttpTimeoutConfig.INFINITE_TIMEOUT_MS
        // Not disabled. A connect that hangs forever is a client that never reaches its own
        // backoff, which is the one way that loop could stop retrying without saying so.
        connectTimeoutMillis = EVENT_STREAM_CONNECT_TIMEOUT_MS
    }
    if (sessionId != null) {
        install(DefaultRequest) {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        }
    }
    if (onUnauthorized != null) reportUnauthorized(onUnauthorized)
}

/** Long enough for a slow LAN, short enough that a dead host reaches the backoff. */
private const val EVENT_STREAM_CONNECT_TIMEOUT_MS = 15_000L

/**
 * The configuration every client shares, so the two factories cannot drift in what they
 * parse or how strictly.
 *
 * `internal` rather than private so a test can build the same configuration over a mock
 * engine. There is no engine-injecting overload of [createHttpClient] on purpose — each
 * target resolves exactly one engine from its classpath, and a parameter for it would be a
 * seam that exists only for tests and can be got wrong in production.
 */
internal fun HttpClientConfig<*>.lunaPinDefaults() {
    install(ContentNegotiation) {
        json(Json { ignoreUnknownKeys = true })
    }
}

/**
 * Call [onUnauthorized] for every 401, and change nothing else.
 *
 * The whole of the 401 hook, in one place, so that what the test exercises is what production
 * installs rather than a re-creation of it. **It must not throw and must not consume the
 * body**: the refusal goes on to become an [ApiFailure] carrying the server's own sentence,
 * and every surface downstream reads that sentence.
 */
internal fun HttpClientConfig<*>.reportUnauthorized(onUnauthorized: () -> Unit) {
    HttpResponseValidator {
        validateResponse { response ->
            if (response.status == HttpStatusCode.Unauthorized) onUnauthorized()
        }
    }
}

/**
 * A request the server refused, carrying the reason it gave.
 *
 * @property status the HTTP status.
 * @property serverMessage the server's own explanation, already written for a
 *   human to read.
 */
class ApiFailure(
    val status: HttpStatusCode,
    val serverMessage: String,
) : Exception("$status: $serverMessage")

/**
 * Decode a successful response, or throw [ApiFailure] carrying what the server
 * actually said.
 *
 * Calling `.body<T>()` on a failed response is a trap: this server's errors are
 * plain text, so Ktor throws NoTransformationFoundException and the *real*
 * message — which the server wrote specifically to be read — is discarded. Check
 * the status first, always.
 */
private suspend inline fun <reified T> HttpResponse.requireSuccess(): T {
    if (!status.isSuccess()) throw ApiFailure(status, bodyAsText())
    return body()
}

/** A response with no body worth parsing. Throws [ApiFailure] on a refusal. */
private suspend fun HttpResponse.requireSuccessNoBody() {
    if (!status.isSuccess()) throw ApiFailure(status, bodyAsText())
}

/**
 * Typed access to the LunaPin server. Transport and nothing else — no caching, no
 * retry policy, no state. Every method is one request; the client's own logic
 * lives one layer up, in `:client`'s `StorageRepository`.
 *
 * Every function is annotated `@Throws`, including [CancellationException], for
 * the reason the architecture doc gives: a Kotlin exception reaching Swift from an
 * unannotated function does not arrive as an `Error`, it terminates the process,
 * and the crash report is close to useless for finding the cause.
 */
interface LunaPinApi {
    /**
     * Who this client is acting as.
     *
     * Does not throw for being signed out — that is a [SessionState] with a null
     * user, not a failure. Only transport and parse failures throw.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun session(): SessionState

    /**
     * Sign in with this deployment's username and password, and answer the session it
     * produced.
     *
     * The session always travels as a cookie, set by the server on this response, and
     * for a browser that is the *only* carrier: [wantsSessionId] is what decides whether
     * the body carries a copy, and the browser must not ask. Nothing here holds the
     * password after the call either — the session is the credential from this point on,
     * so a client that kept the password around would be storing a permanent secret in
     * order to avoid re-reading a form field.
     *
     * Only offer this when [SessionState.isSignInConfigured] said so. A server with no
     * credentials answers 503, which is the honest answer to the operator and a poor
     * thing to show a user.
     *
     * @param username the configured `LUNAPIN_USERNAME`. **Never log it.**
     * @param password the configured `LUNAPIN_PASSWORD`. **Never log it, and do not
     *   keep it** — see above.
     * @param wantsSessionId true only for a caller that has no cookie jar and will
     *   present the session as `Authorization: Bearer` instead — the mobile apps.
     *   **Defaults to false so that forgetting to think about it is the safe outcome**;
     *   see [SignInRequest.wantsSessionId] for why the caller declares this rather than
     *   the server guessing.
     * @throws ApiFailure with 401 for a wrong username *or* a wrong password; the two
     *   are deliberately indistinguishable, so [ApiFailure.serverMessage] cannot be
     *   used to tell a user which half they got wrong. 429 once a caller has failed
     *   several times, carrying a `Retry-After` the server wrote. 503 when this server
     *   has no credentials configured. Every message is written to be shown to a
     *   person as-is.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun signIn(
        username: String,
        password: String,
        wantsSessionId: Boolean = false,
    ): SignInResponse

    /**
     * End this device's session.
     *
     * Idempotent, and safe when already signed out. Ends **this** session only —
     * sessions are per-device, so this does not touch the same account signed in
     * elsewhere.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun signOut()

    /** Every shell setting stored for the caller. Empty for an account that has chosen nothing. */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun uiSettings(): UiSettingsState

    /**
     * Remember one shell setting.
     *
     * @param key one of [UiSettingKeys.persisted]; the server refuses anything
     *   else with a 400, which is a programming error rather than something to
     *   show a user.
     * @param value the blob, stored verbatim.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun setUiSetting(key: String, value: String)

    /**
     * Everything this account has saved, newest first.
     *
     * @throws ApiFailure 401 when nobody is signed in — unlike [session] and
     *   [uiSettings], which both have an honest answer for a stranger. An empty
     *   collection is a statement about an account, and an anonymous caller has not
     *   named one.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun bookmarks(): BookmarkList

    /**
     * One bookmark, by its own [Bookmark.id].
     *
     * What an editor opens with. It used to read [bookmarks] and pick the row out of the
     * answer — one request either way, and the collection was fetched whole by the list
     * window anyway — which stopped being a reasonable trade at a few thousand bookmarks:
     * the server serialised all of them and the client parsed all of them so that one form
     * could be filled in.
     *
     * **A 404 is thrown rather than answered as null**, matching [editBookmark] and
     * [deleteBookmark] on the same route. The surface that can tell "this bookmark is gone"
     * from "we could not ask" is the one holding the window, and it needs both; collapsing
     * the first into a null here would leave the second indistinguishable from a transport
     * failure. `StorageRepository` is where the 404 becomes a null, once, for the callers
     * that want it that way.
     *
     * @param id the bookmark's own [Bookmark.id].
     * @throws ApiFailure 404 for an id this account does not own — identical to the answer
     *   for one that does not exist, deliberately. 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun bookmark(id: Long): Bookmark

    /**
     * Save a link, or fold it into the one already saved under that URL.
     *
     * The route has existed since the collection did — it is what the bookmarklet posts to —
     * and this is its first *typed* caller, because the mobile share sheets are the first
     * thing in Kotlin that saves anything. [SaveBookmarkRequest] argues the upsert at length;
     * the part that matters to a caller is that **it is safe to send twice**. A queued share
     * re-sent after a timeout, or sent from two phones, folds into one bookmark and cannot
     * lose a field, which is the property `BookmarkOutbox` is built on.
     *
     * **Read the answer rather than assuming**: the server assigns the id, normalises the
     * tags, and may have kept a stored title over the blank one just sent. The id in
     * particular is the only way a caller learns which bookmark it just made — there is
     * nothing else to correlate a save with.
     *
     * @param request the link and whatever is known about it. A blank title, blank summary
     *   and empty tags are the ordinary case for a share and erase nothing.
     * @throws ApiFailure 400 with the server's own sentence for a URL it will not store —
     *   written to be shown to a person as-is, and the sentence a refused outbox entry
     *   carries. 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun saveBookmark(request: SaveBookmarkRequest): Bookmark

    /**
     * Change one bookmark, and answer what is now stored.
     *
     * **Everything in [edit] is authoritative, including the blank fields.** This is the
     * one call that can *clear* a title, a note or the last tag; the save route cannot,
     * on purpose. [EditBookmarkRequest] argues the difference, and a caller that sends a
     * half-filled request will empty the fields it left out.
     *
     * **Read the answer rather than assuming the request took effect**, as with
     * [setAccountSettings]: the server normalises tags, so what was sent and what is now
     * held are not the same object.
     *
     * @param id the bookmark's own [Bookmark.id]. The identity for this call, which is
     *   what lets [EditBookmarkRequest.url] change and move the bookmark rather than
     *   duplicate it.
     * @throws ApiFailure 404 for an id this account does not own — identical to the
     *   answer for one that does not exist, deliberately. 400 with the server's own
     *   sentence for a URL it will not store or a link already saved under that URL;
     *   those are written to be shown to a person as-is. 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun editBookmark(id: Long, edit: EditBookmarkRequest): Bookmark

    /**
     * Forget one bookmark.
     *
     * The route has existed since the collection did; this is the first typed caller for
     * it, because the edit window is the first surface with anywhere to put a Delete.
     *
     * **Not idempotent, and deliberately not made so here.** A second delete of the same
     * id is a 404, which reaches the caller as an [ApiFailure] rather than as a shrug —
     * the same 404 that says "somebody else deleted this while you had it open", and the
     * surface that can tell the difference is the one holding the window. Swallowing it
     * would take that signal away from the only code that can use it.
     *
     * @param id the bookmark's own [Bookmark.id].
     * @throws ApiFailure 404 for an id this account does not own — identical to the
     *   answer for one that does not exist, deliberately. 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun deleteBookmark(id: Long)

    /**
     * Read this page again, summarise it and tag it, and answer the row as it now stands.
     *
     * The edit window's *Regenerate* and a failed row's *Try again* — one call, because
     * they are one act. **It returns as soon as the bookmark is in the queue**, not when
     * the summary arrives: the answer's [Bookmark.aiState] is [AiStates.PENDING] and its
     * [Bookmark.aiError] has been cleared. Nothing on this server blocks a request on a
     * model, and a caller that waited would be waiting for up to a minute.
     *
     * How the summary is then noticed is the caller's business, and the list window's
     * answer is a timer that runs only while a visible row is waiting. There is no
     * websocket and no push: one process serving one person does not need a second protocol
     * to deliver a sentence that arrives within a minute.
     *
     * @param id the bookmark's own [Bookmark.id].
     * @throws ApiFailure **503** when this deployment has no model provider key, and **409**
     *   when this account has turned both AI switches off. Two statuses because two
     *   different people can fix them — the operator and the owner — and both carry a
     *   sentence written to be shown as-is, which says which of the two it is. 404 for an id
     *   this account does not own, identical to the answer for one that does not exist. 401
     *   when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun summariseBookmark(id: Long): Bookmark

    /**
     * Queue every bookmark that has **never been tried**, and answer how many that was.
     *
     * The backfill, for a collection that predates the feature. Never-tried rather than
     * merely un-summarised: a bookmark whose page cannot be read has no summary and never
     * will, and including it would make this a retry-everything button spending somebody's
     * quota on pages that already failed. Asking again about one of those is
     * [summariseBookmark].
     *
     * Returns immediately. The queue drains at the worker's own pace — roughly twenty a
     * minute — so a caller should expect four hundred bookmarks to take twenty minutes and
     * should not poll for the whole of it.
     *
     * @throws ApiFailure the same 503 and 409 [summariseBookmark] answers, with the same
     *   sentences. 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun summariseMissing(): SummariseMissingResult

    /**
     * How far this account's enrichment queue has got.
     *
     * The other half of [summariseMissing], which starts hours of work and answers once. A
     * caller polls this — every couple of seconds is what the settings panel does — and reads
     * [EnrichmentProgress.isRunning] rather than remembering whether it pressed the button:
     * the counts are right after a reload, after a server restart, and when somebody started
     * the backfill on another device, and a remembered flag is right until any of those.
     *
     * **Never refused for having no key or having the switches off**, unlike [summariseMissing]
     * — a queue can hold rows in either state and counting them is honest in both. See
     * [ApiRoutes.AI_PROGRESS].
     *
     * @throws ApiFailure 401 when nobody is signed in, for [bookmarks]' reason.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun enrichmentProgress(): EnrichmentProgress

    /**
     * Empty this account's enrichment queue, and answer how many bookmarks came out of it.
     *
     * The brakes on [summariseMissing]. It touches nothing but the `pending` rows: the one
     * the worker has already claimed finishes, because its model call is paid for either way.
     *
     * **Safe and undoable**, which is why no caller should put a confirmation in front of it:
     * the cancelled rows are untried, so [summariseMissing] counts and re-queues them exactly
     * as it did before, and pressing *Backfill* again resumes.
     *
     * **Not refused for the reasons [summariseMissing] is refused.** An account that has just
     * turned both AI switches off is the likeliest one to want its queue emptied.
     *
     * @throws ApiFailure 401 when nobody is signed in, for [bookmarks]' reason.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun cancelBackfill(): CancelBackfillResult

    /**
     * How much this account has asked the model for this month, and what a backfill would
     * pick up right now.
     *
     * An account that has enriched nothing gets four zeroes rather than a failure — see
     * [AiUsage], and [accountSettings] for the same decision about settings that were never
     * written.
     *
     * @throws ApiFailure 401 when nobody is signed in, for [bookmarks]' reason.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun aiUsage(): AiUsage

    /**
     * Make one minimal real call to the model provider and answer what came back.
     *
     * *Test connection*, and the only call in this interface that deliberately costs the
     * operator something. It answers the question [accountSettings] cannot:
     * [AccountSettings.aiAvailable] says a key is set, this says whether it works.
     *
     * **A rejected key is a returned [AiTestResult] with `ok = false`, not a thrown
     * failure.** The test ran and has a sentence to show; the throws below are the states
     * where no call was made at all.
     *
     * @throws ApiFailure **503** when this deployment has no key — nothing to test, and a
     *   call that could not work would report a provider problem for a missing variable.
     *   **429** with the wait in its message once it has been pressed several times in a
     *   few minutes, because unlike everything else here each press spends money. 401 when
     *   nobody is signed in. Every message is written to be shown as-is, and **none of them
     *   contains the key or any part of it**.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun testAi(): AiTestResult

    /**
     * Every tag this account uses, most-used first.
     *
     * What a `#tag` field draws its menu from — see [TagInputRules], which is where the
     * behaviour that menu obeys is written down. The counts are the point: they are what
     * the order is by and what each row shows, so a near-duplicate is not picked by
     * accident.
     *
     * One call rather than a query per keystroke. An account's tag vocabulary is a few
     * dozen short strings, it changes only when a bookmark is saved or edited, and
     * filtering it against what is being typed is a client-side operation on a list
     * already in hand — a request per keystroke would put a round trip between a letter
     * and the menu that answers it.
     *
     * @throws ApiFailure 401 when nobody is signed in, for [bookmarks]' reason: a tag
     *   list is a statement about an account, and an anonymous caller has not named one.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun tags(): List<TagUse>

    /**
     * How large this account's vocabulary is, how much of it is used once, and whether the
     * last tidy can still be undone.
     *
     * What the settings window's Vocabulary panel draws before anything is pressed. A route of
     * its own rather than counting [tags] client-side, because two of the four numbers are not
     * in that answer: how many bookmarks the tags file, and whether a snapshot exists.
     *
     * @throws ApiFailure 401 when nobody is signed in, for [bookmarks]' reason.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun tidyStatus(): TidyStatus

    /**
     * Work out what this account's vocabulary could be tidied into. **Changes nothing.**
     *
     * Rules first, then a model over what they left. The rules find singular/plural pairs,
     * tags used once and shared-word clusters; the model — when this deployment has a key —
     * is shown the remainder and asked for a mapping. [TidyPlan.modelConsulted] says which
     * happened, and a deployment with no key gets a smaller plan rather than a failure.
     *
     * **The only call in this interface that can spend the operator's money**, alongside
     * [testAi], and the only one throttled for it.
     *
     * @throws ApiFailure **429** with the wait in its message once it has been pressed several
     *   times in a few minutes — and only on a deployment that has a key, since one without
     *   spends nothing. 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun planTidy(): TidyPlan

    /**
     * What this selection would cost, counted against the live collection. **Changes nothing.**
     *
     * What a dialog asks after somebody unticks a row. The numbers cannot be recomputed by a
     * caller: how many bookmarks would be left with no tags at all needs every bookmark's tag
     * rows, and a client holding those would be holding the collection in order to draw a
     * summary line.
     *
     * It runs the same validation [applyTidy] runs, so a merge into a tag nobody owns previews
     * as the drop it would become — which is what makes the preview a description of the
     * button rather than of the request.
     *
     * @throws ApiFailure 400 with the server's own sentence for a selection that cannot be
     *   applied at all — a chained merge, or one tag folded two ways. 401 when nobody is
     *   signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun previewTidy(selection: TidySelection): TidyImpact

    /**
     * Apply this selection. **Destructive, and the largest thing in this interface.**
     *
     * One transaction over a snapshot taken inside it, so there is no instant at which a tidy
     * has half happened and none at which it has happened with nothing to go back to.
     * [undoTidy] is that way back and it is a single step.
     *
     * **No surface calling this should ask *are you sure*.** The dialog already showed a
     * preview computed from these exact numbers and an undo is one press away; a confirmation
     * over a previewed, reversible action is a dialog people learn to dismiss without reading.
     *
     * The selection is re-validated by the server against the live vocabulary, so what comes
     * back may describe slightly less than what was sent — a tag that stopped existing while
     * the dialog was open is quietly not folded. **Read the answer** rather than assuming the
     * request took effect, exactly as with [setAccountSettings].
     *
     * @throws ApiFailure 400 with the server's own sentence for a selection it will not
     *   repair — a chain, or a contradiction. 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun applyTidy(selection: TidySelection): TidyResult

    /**
     * Put the tags back as they were before the last tidy.
     *
     * **One step, and the snapshot is spent by taking it.** A second press answers zeroes,
     * which is an ordinary answer rather than a failure — and so does a press by an account
     * that has never tidied.
     *
     * A bookmark deleted since the tidy is not resurrected and its tags are not restored; see
     * [UndoTidyResult.tags], where the gap is stated.
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun undoTidy(): UndoTidyResult

    /**
     * What this account has chosen about summaries, tags and saving.
     *
     * An account that has never chosen anything answers the defaults rather than
     * failing, so a caller never has to tell "no settings" from "settings that happen to
     * be the defaults" — see [AccountSettings], which argues why those are one state.
     *
     * [AccountSettings.aiAvailable] is the only thing this — or anything — reports about
     * the deployment's model provider key.
     *
     * @throws ApiFailure 401 when nobody is signed in, for [bookmarks]' reason: a
     *   preference belongs to an account, and an anonymous caller has not named one.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun accountSettings(): AccountSettings

    /**
     * Store all of them, and answer what was stored.
     *
     * The whole set in one call rather than a field at a time, matching the route: this
     * is a form applied, so it either landed or it did not.
     *
     * **Read the answer rather than assuming the request took effect.** The server
     * normalises — [SetAccountSettingsRequest.saveDefaultTags] comes back trimmed,
     * `#`-prefixed, lower-cased and sorted — so what was sent and what is now held are
     * not always the same strings.
     *
     * @throws ApiFailure 400 with the server's own sentence for a value outside what it
     *   accepts (a tag count outside 0–10, an unknown summary length or language). Those
     *   messages are written to be shown to a person as-is. 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun setAccountSettings(settings: SetAccountSettingsRequest): AccountSettings

    // ── Agent access ─────────────────────────────────────────────────────────
    //
    // Three calls, and all three answer the *whole* [McpState] rather than a fragment — so a
    // client re-renders from one object and cannot end up holding a switch position from one
    // response and a connections list from another. See [McpState].
    //
    // There is deliberately no `connect` here, and there must never be. The OAuth flow is
    // always started by the agent: the agent has to be the client, hold the PKCE verifier and
    // receive the code at its own redirect URI. This surface only ever displays a URL and
    // lists the result.

    /**
     * The Connections tab: whether agents may act as this account, the `/mcp` URL to paste
     * into one, and what is connected.
     *
     * @throws ApiFailure 401 when nobody is signed in, for [bookmarks]' reason.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun mcpState(): McpState

    /**
     * Turn agent access on or off, and answer the state that landed.
     *
     * **Read the answer rather than assuming the request took effect**, exactly as for
     * [setAccountSettings]: the server re-reads the row after writing it, so what comes back
     * is a report rather than an echo.
     *
     * Turning it off revokes nothing — see [ApiRoutes.MCP_ENABLED].
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun setMcpEnabled(isEnabled: Boolean): McpState

    /**
     * Cut one agent off, and answer what is left.
     *
     * Idempotent: a `clientId` this account has nothing for is not an error, because the
     * end state it asks for already holds.
     *
     * @throws ApiFailure 401 when nobody is signed in.
     */
    @Throws(ApiFailure::class, CancellationException::class, Exception::class)
    suspend fun revokeMcpConnection(clientId: String): McpState
}

/**
 * The production [LunaPinApi].
 *
 * @param baseUrl prefix for every request. Defaults to `""` — a relative URL —
 *   which is correct for the browser bundle: the server that serves the bundle is
 *   the server the bundle talks to, so there is no cross-origin request here and
 *   no CORS to configure. The mobile apps pass an absolute URL, built from the scheme,
 *   host and port their user typed.
 * @param httpClient the transport; defaults to a fresh [createHttpClient]. A client
 *   holding a session — `createHttpClient(sessionId)` — is what makes every call here
 *   authenticated for a caller with no cookie jar; nothing in this class knows which of
 *   the two it was given.
 */
class HttpLunaPinApi(
    private val baseUrl: String = "",
    private val httpClient: HttpClient = createHttpClient(),
) : LunaPinApi {

    override suspend fun session(): SessionState =
        httpClient.get("$baseUrl${ApiRoutes.SESSION}").requireSuccess()

    override suspend fun signIn(
        username: String,
        password: String,
        wantsSessionId: Boolean,
    ): SignInResponse =
        httpClient.post("$baseUrl${ApiRoutes.AUTH_SIGN_IN}") {
            contentType(ContentType.Application.Json)
            // A JSON body, so the credential is never in the request line. A GET with
            // query parameters would put the password in every access log between here
            // and the server, and in the browser's own history.
            setBody(
                SignInRequest(
                    username = username,
                    password = password,
                    wantsSessionId = wantsSessionId,
                ),
            )
        }.requireSuccess()

    override suspend fun signOut() {
        httpClient.post("$baseUrl${ApiRoutes.AUTH_SIGN_OUT}").requireSuccessNoBody()
    }

    override suspend fun uiSettings(): UiSettingsState =
        httpClient.get("$baseUrl${ApiRoutes.USER_UI_SETTINGS}").requireSuccess()

    override suspend fun setUiSetting(key: String, value: String) {
        httpClient.post("$baseUrl${ApiRoutes.USER_UI_SETTINGS}") {
            contentType(ContentType.Application.Json)
            setBody(SetUiSettingRequest(key = key, value = value))
        }.requireSuccessNoBody()
    }

    override suspend fun bookmarks(): BookmarkList =
        httpClient.get("$baseUrl${ApiRoutes.BOOKMARKS}").requireSuccess()

    override suspend fun bookmark(id: Long): Bookmark =
        httpClient.get("$baseUrl${ApiRoutes.bookmarkById(id)}").requireSuccess()

    override suspend fun saveBookmark(request: SaveBookmarkRequest): Bookmark =
        httpClient.post("$baseUrl${ApiRoutes.BOOKMARKS}") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.requireSuccess()

    override suspend fun editBookmark(id: Long, edit: EditBookmarkRequest): Bookmark =
        httpClient.put("$baseUrl${ApiRoutes.bookmarkById(id)}") {
            contentType(ContentType.Application.Json)
            setBody(edit)
        }.requireSuccess()

    override suspend fun deleteBookmark(id: Long) {
        httpClient.delete("$baseUrl${ApiRoutes.bookmarkById(id)}").requireSuccessNoBody()
    }

    override suspend fun summariseBookmark(id: Long): Bookmark =
        httpClient.post("$baseUrl${ApiRoutes.bookmarkSummarise(id)}").requireSuccess()

    override suspend fun summariseMissing(): SummariseMissingResult =
        httpClient.post("$baseUrl${ApiRoutes.BOOKMARKS_SUMMARISE_MISSING}").requireSuccess()

    override suspend fun enrichmentProgress(): EnrichmentProgress =
        httpClient.get("$baseUrl${ApiRoutes.AI_PROGRESS}").requireSuccess()

    override suspend fun cancelBackfill(): CancelBackfillResult =
        httpClient.post("$baseUrl${ApiRoutes.AI_CANCEL_BACKFILL}").requireSuccess()

    override suspend fun aiUsage(): AiUsage =
        httpClient.get("$baseUrl${ApiRoutes.USER_AI_USAGE}").requireSuccess()

    override suspend fun testAi(): AiTestResult =
        httpClient.post("$baseUrl${ApiRoutes.AI_TEST}").requireSuccess()

    override suspend fun tags(): List<TagUse> =
        httpClient.get("$baseUrl${ApiRoutes.TAGS}").requireSuccess()

    override suspend fun tidyStatus(): TidyStatus =
        httpClient.get("$baseUrl${ApiRoutes.TAGS_TIDY}").requireSuccess()

    override suspend fun planTidy(): TidyPlan =
        httpClient.post("$baseUrl${ApiRoutes.TAGS_TIDY_PLAN}").requireSuccess()

    override suspend fun previewTidy(selection: TidySelection): TidyImpact =
        httpClient.post("$baseUrl${ApiRoutes.TAGS_TIDY_PREVIEW}") {
            contentType(ContentType.Application.Json)
            setBody(selection)
        }.requireSuccess()

    override suspend fun applyTidy(selection: TidySelection): TidyResult =
        httpClient.post("$baseUrl${ApiRoutes.TAGS_TIDY_APPLY}") {
            contentType(ContentType.Application.Json)
            setBody(selection)
        }.requireSuccess()

    override suspend fun undoTidy(): UndoTidyResult =
        httpClient.post("$baseUrl${ApiRoutes.TAGS_TIDY_UNDO}").requireSuccess()

    override suspend fun accountSettings(): AccountSettings =
        httpClient.get("$baseUrl${ApiRoutes.USER_SETTINGS}").requireSuccess()

    override suspend fun setAccountSettings(settings: SetAccountSettingsRequest): AccountSettings =
        httpClient.put("$baseUrl${ApiRoutes.USER_SETTINGS}") {
            contentType(ContentType.Application.Json)
            setBody(settings)
        }.requireSuccess()

    override suspend fun mcpState(): McpState =
        httpClient.get("$baseUrl${ApiRoutes.MCP}").requireSuccess()

    override suspend fun setMcpEnabled(isEnabled: Boolean): McpState =
        httpClient.post("$baseUrl${ApiRoutes.MCP_ENABLED}") {
            contentType(ContentType.Application.Json)
            setBody(McpEnabledRequest(isEnabled = isEnabled))
        }.requireSuccess()

    override suspend fun revokeMcpConnection(clientId: String): McpState =
        httpClient.delete("$baseUrl${ApiRoutes.mcpConnection(clientId)}").requireSuccess()
}
